import ActionPerformer from "@/utils/moreMenu/ActionPerformer.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import MoreMenuItem from "@/utils/UIConstants/MoreMenuItem.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import Translator from "@/utils/common/Translator.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import EditViewToolbarUtils from "@/utils/editView/EditViewToolbarUtils.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import {Dialog} from "quasar";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.vue";
import QuestionField = namaTypes.QuestionField;
import FieldMetaData = namaTypes.FieldMetaData;
import GUIActionInfo = namaTypes.GUIActionInfo;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;

export default class ActionHandler {
  private readonly _actionInfo: ActionPlaceholder;
  private performer: ActionPerformer;
  private metaData: namaTypes.NaMaMetaData;
  private presenter?: QuestionFieldsPresenter;
  private dialogBox?: DialogHandler;
  private readonly _idPrefix: string;
  private _runGuiPostActionAfterSuccess: boolean = false;
  constructor(actionInfo: ActionPlaceholder, metaData: namaTypes.NaMaMetaData, idPrefix: string, performer: ActionPerformer) {
    this._actionInfo = actionInfo;
    this.metaData = metaData;
    this._idPrefix = idPrefix;
    this.performer = performer;
  }

  async performAction(fromAnotherAction?: boolean) {
    if (!fromAnotherAction)
      await FieldWidgetUtil.saveEditingFieldsAndGrids();
    if (!await this.confirmActionIfNeeded())
      return;
    if (ObjectChecker.areEqual(this._actionInfo?.id, "PayInvoice")) {
      await this.performer.payInvoice(undefined);
      return;
    }
    if (ObjectChecker.areEqual(this._actionInfo?.id, "FetchLastTerminalPayTrnx")) {
      await this.performer.fetchLastTerminalPayTrnx();
      return;
    }
    if (ObjectChecker.areEqual(this._actionInfo.id, "SignDocuments")) {
      await this.performer.signDocuments(false);
      return;
    }
    if (ObjectChecker.areEqual(this._actionInfo.id, "SignSelectedDocuments")) {
      await this.performer.signDocuments(true);
      return;
    }
    if (ObjectChecker.areEqual(this._actionInfo?.id, "editDashboardElements")) {
      this.performer.editDashboardElements();
      return;
    }
    if (ObjectChecker.areEqual(this._actionInfo?.id, "editChartConfig")) {
      this.performer.editChartConfig();
      return;
    }
    if (ObjectChecker.areEqual(this._actionInfo?.id, "editMappings")) {
      this.performer.editMappings();
      return;
    }
    if (ObjectChecker.areEqual(this._actionInfo?.id, EditViewToolbarUtils.getCurrentApprovalAction().id)) {
      await this.performer.showCurrentApprovalCase();
      return;
    }
    if (ObjectChecker.areEqual(this._actionInfo?.id, "readFirstWeight")) {
      await this.performer.readFirstWeight();
      return;
    }
    if (ObjectChecker.areEqual(this._actionInfo?.id, "readSecondWeight")) {
      await this.performer.readSecondWeight();
      return;
    }
    if (ObjectChecker.isNotEmptyOrNull(this._actionInfo?.customization?.bulkEditId)) {
      await this.performer.bulkEdit(this._actionInfo);
      return;
    }
    let guiPostActionsId = this._actionInfo?.customization?.guiPostActionsId;
    let systemAction = ObjectChecker.isNotEmptyOrNull(guiPostActionsId)
      ? this.metaData.entityActions?.actions?.find(l => l.id === this._actionInfo.id)
      : undefined;
    if (ObjectChecker.isNotEmptyOrNull(guiPostActionsId) && !systemAction) {
      await this.performer.runCustomGuiPostAction(this._actionInfo);
      await this.performer.runGuiPostActionForSystemAction(this._actionInfo);
      return;
    }
    if (this._actionInfo?.customization?.empty === false && !systemAction) {
      if (await this.performer.runCustomAction(this._actionInfo))
        await this.performer.runGuiPostActionForSystemAction(this._actionInfo);
      return;
    }
    let action = systemAction ?? this.metaData.entityActions?.actions?.find(l => l.id === this._actionInfo.id);
    if (!action) {
      useErrorsStore().pushErrorStr("Could not find gui action definition with id:" + this._actionInfo.id)
      return;
    }
    if (action.mustBeSaved && this.performer.isRecordNotSaved()) {
      useErrorsStore().pushErrorStr("actionModifiedEntity")
      return;
    }
    this._runGuiPostActionAfterSuccess = Boolean(systemAction);
    if (ObjectChecker.isNotEmptyOrNull(action.questions)) {
      await this.performActionWithQuestions(action);
    } else {
      await this.performSystemAction([]);
    }
  }

  private async performSystemAction(questionFields: namaTypes.DTOFilledQuestionField[]) {
    const runGuiPostActionAfterSuccess = this._runGuiPostActionAfterSuccess;
    await this.performer.performAction(this._actionInfo, questionFields, async success => {
      if (!success)
        return;
      if (runGuiPostActionAfterSuccess)
        await this.performer.runCustomGuiPostAction(this._actionInfo);
      await this.performer.runGuiPostActionForSystemAction(this._actionInfo);
    });
  }

  private async confirmActionIfNeeded(): Promise<boolean> {
    const customization = this._actionInfo?.customization;
    const message = Translator.getName(customization?.confirmationMessageAr, customization?.confirmationMessageEn);
    if (ObjectChecker.isEmptyOrNull(message))
      return true;
    return await new Promise<boolean>((resolve) => {
      Dialog.create({
        component: ConfirmDialog,
        componentProps: {
          title: Translator.translateId("confirm"),
          confirmationText: message,
          ok: Translator.translateId("ok"),
          cancel: Translator.translateId("cancel"),
          isPersistent: true
        }
      }).onOk(() => resolve(true)).onCancel(() => resolve(false)).onDismiss(() => resolve(false));
    });
  }

  private async performActionWithQuestions(action: GUIActionInfo) {
    let questionFields: QuestionField[] = [];
    for (let questionFieldId of action.questions!) {
      let questionField: QuestionField = {};
      let questionFieldMetaData: FieldMetaData | undefined =
        action.questionsMetadata && action.questionsMetadata.length > 0 ?
          action.questionsMetadata.find(l => l.id === questionFieldId)
          : {};
      if (!questionFieldMetaData)
        questionFieldMetaData = this.metaData.fieldsMetaData?.find(l => l.id === questionFieldId);
      if (!questionFieldMetaData) {
        console.log(`Could not find question metadata for ${questionFieldId} in action ${this._actionInfo.id}`)
        continue;
      }
      questionField.metaData = questionFieldMetaData;
      questionField.id = questionFieldId;
      questionFields.push(questionField);
    }
    if (ObjectChecker.isNotEmptyOrNull(questionFields)) {
      if (ObjectChecker.isEmptyOrNull(action.actionInitializerClassName)) {
        this.presenter = new QuestionFieldsPresenter(this.idPrefix);
        await this.showNamaDialogBox(questionFields, []);
      } else {
        this.performer.calcDefaultsOf(this._actionInfo,
          async (defaults: DTOFilledQuestionField[]) => {
            this.presenter = new QuestionFieldsPresenter(this.idPrefix);
            await this.showNamaDialogBox(questionFields, defaults);
          });
      }
    }
  }

  private async showNamaDialogBox(questionFields: QuestionField[], defaultValues: DTOFilledQuestionField[]) {
    if (!this.presenter)
      return;
    this.presenter.performer = this.performer;
    await this.presenter.edit(questionFields, defaultValues);
    let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (record: Record<string, any>) => {
      let loadingStore = useLoadingStore();
      loadingStore.startLoading();
      await this.okClicked();
      loadingStore.stopLoading();
    });
    let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
    });
    this.dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateTitledId(this.idPrefix, this.actionInfo));
    if (this.actionInfo.id === IdsAndConstants.SET_NEW_PASSWORD_ACTION)
      this.dialogBox.alwaysAllowed = true;
    await this.dialogBox.createPresenter(this.presenter);
    await this.dialogBox.runDialogBox();
  }

  async okClicked() {
    if (!this.presenter)
      return;
    if (ObjectChecker.areEqual(this._actionInfo.id, "PayPartOfInvoice")) {
      let questionField: DTOFilledQuestionField[] = this.presenter.getQuestionFields()
        .filter(q => ObjectChecker.areEqual("invoicePaymentValue", q.id));
      if (ObjectChecker.isNotEmptyOrNull(questionField) && ObjectChecker.isNotEmptyOrNull(questionField[0])
        && ObjectChecker.isNotEmptyOrNull(questionField[0].value))
        await this.performer.payInvoice(Number.parseFloat(questionField[0].value));
      return;
    }
    await this.performSystemAction(this.presenter.getQuestionFields());
  }

  toMoreMenuItem(): MoreMenuItem {
    return MoreMenuItem.createFromActionHandler(this);
  }


  get actionInfo(): ActionPlaceholder {
    return this._actionInfo;
  }


  get idPrefix(): string {
    return this._idPrefix;
  }
}

export type ActionPlaceholder = namaTypes.ListViewActionPlaceHolder | namaTypes.GUIActionPlaceholder;
